import {FC, useRef} from 'react'
import {ISelectOptions} from '../cors/_models'
import { useIntl } from 'react-intl'
import Select, { StylesConfig } from 'react-select'

type IProps = {
  label?: string
  isRequired?: boolean
  id: string
  type?: string
  onChange?: any
  value?: any
  options?: ISelectOptions[]
  errorMsg?: string
  hasError?: boolean
  readOnly?: boolean
  min?: string
  hint?:string
  placeholder?: string;
  showLabel?: boolean;
  classNameEnable?: React.CSSProperties;
  isSearchable?: boolean;
  maxLength?: number;
  inputMode?: 'numeric' | 'decimal' | 'text';
}

const InputField: FC<IProps> = ({
  label,
  isRequired,
  id,
  type,
  onChange,
  value,
  options,
  errorMsg,
  hasError,
  readOnly,
  min,
  hint,
  placeholder,
  showLabel= true,
  classNameEnable,
  isSearchable,
  maxLength,
  inputMode,
}) => {
  const {formatMessage} = useIntl();
  const inputRef = useRef<any>('')

  return (
    <div>
      {label && showLabel && (
        <label className='form-label fw-bold fs-3'>
          {label} {isRequired && <sup className='text-danger'>*</sup>} {hint && <small style={{fontSize: '12px'}} className='text-gray-600'>{hint}</small>}
        </label>
      )}
      <div>
        {type && type == 'select' ? (
          <>
            {isSearchable ? (
              <Select
                value={options?.find(option => option.value === value)}
                onChange={(selectedOption: any) => onChange({ target: { id, value: selectedOption?.value || '' } })}
                options={options}
                className='w-100'
                classNamePrefix='select'
                isSearchable={true}
                isClearable={false}
                placeholder=''
                isDisabled={readOnly}
                styles={{
                  control: (baseStyles, state) => ({
                    ...baseStyles,
                    backgroundColor: '#f8fafc',
                    borderColor: state.isFocused ? '#2563eb' : '#e2e8f0',
                    height: '43px',
                    boxShadow: state.isFocused ? '0 0 0 3px rgba(37, 99, 235, 0.12)' : 'none',
                    borderRadius: '8px',
                    padding: '0 0.75rem',
                    fontSize: '1.1rem',
                    display: 'flex',
                    alignItems: 'center',
                    '&:hover': {
                      borderColor: state.isFocused ? '#2563eb' : '#cbd5e1'
                    }
                  }),
                  // Drop react-select's default vertical bar before the dropdown
                  // arrow — matches the Add page's clean inputs.
                  indicatorSeparator: () => ({
                    display: 'none'
                  }),
                  menu: (baseStyles) => ({
                    ...baseStyles,
                    backgroundColor: '#ffffff',
                    zIndex: 9999
                  }),
                  option: (baseStyles, { isFocused }) => ({
                    ...baseStyles,
                    backgroundColor: isFocused ? '#E4E6EF' : '#ffffff',
                    padding: '0.75rem 1rem',
                    fontSize: '1.1rem',
                    color: '#3F4254',
                    '&:hover': {
                      backgroundColor: '#F3F7F9'
                    }
                  }),
                  singleValue: (baseStyles) => ({
                    ...baseStyles,
                    color: '#3F4254',
                    margin: '0'
                  }),
                  input: (baseStyles) => ({
                    ...baseStyles,
                    color: '#3F4254',
                    margin: '0',
                    padding: '0'
                  }),
                  valueContainer: (baseStyles) => ({
                    ...baseStyles,
                    padding: '0'
                  }),
                  dropdownIndicator: (baseStyles) => ({
                    ...baseStyles,
                    color: '#94ADC7',
                    padding: '0 8px',
                    '&:hover': {
                      color: '#94ADC7'
                    }
                  })
                } as StylesConfig}
              />
            ) : (
              <select
                value={value}
                onChange={onChange}
                className='form-control w-100'
                id={id}
                name={id}
                ref={inputRef}
                data-isrequired={isRequired}
              >
                {options &&
                  options.map((item: ISelectOptions) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
              </select>
            )}
            {isRequired && hasError && (
              <label style={{fontSize: '12px'}} className='form-label fw-bold text-danger'>
                {errorMsg ? errorMsg : formatMessage({ id: "required_field" })}
              </label>
            )}
          </>
        ) : (
          <>
            {type === 'number' ? (
              <input
                value={value}
                onChange={onChange}
                className={`form-control w-100 no-arrows ${readOnly && 'form-control-solid'}`}
                style={classNameEnable}
                type={type || 'text'}
                id={id}
                name={id}
                readOnly={readOnly || false}
                ref={inputRef}
                data-isrequired={isRequired}
                min={min}
                placeholder={placeholder}
                onWheel={(e: any) => e.target.blur()}
                autoComplete='off'
              />
            ) : type === 'datetime-local' ? (
              <input
                value={value || ''}
                onChange={onChange}
                className={`form-control w-100 no-arrows ${readOnly && 'form-control-solid'}`}
                style={classNameEnable}
                type="datetime-local"
                id={id}
                name={id}
                readOnly={readOnly || false}
                ref={inputRef}
                data-isrequired={isRequired}
                min={min}
                placeholder={placeholder}
                autoComplete='off'
              />
            ) : (
              <input
                value={value || ''}
                onChange={onChange}
                className={`form-control w-100 no-arrows ${readOnly && 'form-control-solid'}`}
                style={classNameEnable}
                type={type || 'text'}
                id={id}
                name={id}
                readOnly={readOnly || false}
                ref={inputRef}
                data-isrequired={isRequired}
                placeholder={placeholder}
                min={min}
                maxLength={maxLength}
                inputMode={inputMode}
                autoComplete='off'
              />
            )}

            {isRequired && hasError && (
              <label style={{fontSize: '12px'}} className='form-label fw-bold text-danger'>
                {errorMsg ? errorMsg : formatMessage({ id: "required_field" })}
              </label>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default InputField
